import { f0FormField } from '@factorialco/f0-react'
import type { F0SelectItemProps } from '@factorialco/f0-react'
import type {
  AttendanceCustomTimeRangeDayEnum,
  AttendanceShiftLocationTypeEnum,
  PayrollConceptSelectFieldFragment,
} from 'generated/resources/graphql'
import { z } from 'zod'

import i18n from 'modules/core/lib/i18n'

import {
  FixedAmountInputWrapper,
  MaximumAmountInputWrapper,
  PerWorkedDayAmountInputWrapper,
  PerWorkedDayDaysCountHeader,
} from 'modules/contracts/components/FlowPage/Steps/RoleLevelSalary/_AdditionalCompensations/AdditionalCompensationWizard/AdditionalCompensationWizardFields'

import {
  type BasicContext,
  DURATION_FIELDS,
  FIXED_AMOUNT_STRATEGY,
  PER_WORKED_DAY_DAYS_COUNTED_FROM_VALUES,
  PER_WORKED_DAY_ELIGIBLE_DAY_VALUES,
  PER_WORKED_DAY_STRATEGY,
  PER_WORKED_DAY_WORK_LOCATION_VALUES,
  VARIABLE_AMOUNT_STRATEGY,
  isNumber,
  normalizeDefinitionNumber,
} from './additionalCompensationWizard.shared'

type LeaveTypeOption = {
  id: string
  label: string
}

const buildDaysCountedFromOptions = (): Array<
  F0SelectItemProps<(typeof PER_WORKED_DAY_DAYS_COUNTED_FROM_VALUES)[number]>
> => [
  {
    value: PER_WORKED_DAY_DAYS_COUNTED_FROM_VALUES[0],
    label: i18n.t(
      'contract_versions.additional_compensation.create_additional_compensation_wizard.definition.per_worked_day.days_counted_from.options.previous_month'
    ),
  },
]

const getWorkLocationOptionLabel = (value: AttendanceShiftLocationTypeEnum) => {
  switch (value) {
    case 'office':
      return i18n.t(
        'contract_versions.additional_compensation.create_additional_compensation_wizard.definition.per_worked_day.work_locations.options.office'
      )
    case 'work_from_home':
      return i18n.t(
        'contract_versions.additional_compensation.create_additional_compensation_wizard.definition.per_worked_day.work_locations.options.work_from_home'
      )
    case 'business_trip':
      return i18n.t(
        'contract_versions.additional_compensation.create_additional_compensation_wizard.definition.per_worked_day.work_locations.options.business_trip'
      )
    default:
      return String(value)
  }
}

const buildWorkLocationOptions = (): Array<
  F0SelectItemProps<(typeof PER_WORKED_DAY_WORK_LOCATION_VALUES)[number]>
> =>
  PER_WORKED_DAY_WORK_LOCATION_VALUES.map((value) => ({
    value,
    label: getWorkLocationOptionLabel(value),
  }))

const getEligibleDayOptionLabel = (value: AttendanceCustomTimeRangeDayEnum) => {
  switch (value) {
    case 'monday':
      return i18n.t(
        'contract_versions.additional_compensation.create_additional_compensation_wizard.definition.per_worked_day.eligible_days.options.monday'
      )
    case 'tuesday':
      return i18n.t(
        'contract_versions.additional_compensation.create_additional_compensation_wizard.definition.per_worked_day.eligible_days.options.tuesday'
      )
    case 'wednesday':
      return i18n.t(
        'contract_versions.additional_compensation.create_additional_compensation_wizard.definition.per_worked_day.eligible_days.options.wednesday'
      )
    case 'thursday':
      return i18n.t(
        'contract_versions.additional_compensation.create_additional_compensation_wizard.definition.per_worked_day.eligible_days.options.thursday'
      )
    case 'friday':
      return i18n.t(
        'contract_versions.additional_compensation.create_additional_compensation_wizard.definition.per_worked_day.eligible_days.options.friday'
      )
    case 'saturday':
      return i18n.t(
        'contract_versions.additional_compensation.create_additional_compensation_wizard.definition.per_worked_day.eligible_days.options.saturday'
      )
    case 'sunday':
      return i18n.t(
        'contract_versions.additional_compensation.create_additional_compensation_wizard.definition.per_worked_day.eligible_days.options.sunday'
      )
    case 'bank_holiday':
      return i18n.t(
        'contract_versions.additional_compensation.create_additional_compensation_wizard.definition.per_worked_day.eligible_days.options.bank_holiday'
      )
    default:
      return String(value)
  }
}

const buildEligibleDayOptions = (): Array<
  F0SelectItemProps<(typeof PER_WORKED_DAY_ELIGIBLE_DAY_VALUES)[number]>
> =>
  PER_WORKED_DAY_ELIGIBLE_DAY_VALUES.map((value) => ({
    value,
    label: getEligibleDayOptionLabel(value),
  }))

const buildDefinitionSchema = ({
  payrollConcepts,
  basicContext,
  leaveTypeOptions,
}: {
  payrollConcepts: PayrollConceptSelectFieldFragment[]
  basicContext: BasicContext
  leaveTypeOptions: LeaveTypeOption[]
}) => {
  const daysCountedFromOptions = buildDaysCountedFromOptions()
  const workLocationOptions = buildWorkLocationOptions()
  const eligibleDayOptions = buildEligibleDayOptions()

  return z
    .object({
      fixedAmount: f0FormField(z.number().nullable().optional(), {
        label: i18n.t(
          'contract_versions.additional_compensation.create_additional_compensation_wizard.definition.fixed_amount.title'
        ),
        fieldType: 'custom',
        placeholder: i18n.t(
          'contract_versions.additional_compensation.create_additional_compensation_wizard.definition.fixed_amount.placeholder'
        ),
        renderIf: () => basicContext.amountStrategyType === FIXED_AMOUNT_STRATEGY,
        render: ({
          value,
          onChange,
          error,
        }: {
          value: unknown
          onChange: (val: unknown) => void
          error?: string
        }) => {
          const numericValue = isNumber(value) ? value : undefined

          return (
            <FixedAmountInputWrapper
              value={numericValue}
              onChange={(numberValue) => onChange(numberValue)}
              onClear={() => onChange(null)}
              error={error}
              payrollConcepts={payrollConcepts}
              selectedPayrollConceptId={basicContext.payrollConceptId}
              required
            />
          )
        },
      }),
      maximumAmountPerCycle: f0FormField(z.number().nullable().optional(), {
        label: i18n.t(
          'contract_versions.additional_compensation.create_additional_compensation_wizard.definition.maximum_amount_per_cycle.label'
        ),
        fieldType: 'custom',
        placeholder: i18n.t(
          'contract_versions.additional_compensation.create_additional_compensation_wizard.definition.variable_amount.placeholder'
        ),
        renderIf: () => basicContext.amountStrategyType === VARIABLE_AMOUNT_STRATEGY,
        render: ({
          value,
          onChange,
          error,
        }: {
          value: unknown
          onChange: (val: unknown) => void
          error?: string
        }) => {
          const numericValue = isNumber(value) ? value : undefined

          return (
            <MaximumAmountInputWrapper
              value={numericValue}
              onChange={(numberValue) => onChange(numberValue)}
              onClear={() => onChange(null)}
              error={error}
              payrollConcepts={payrollConcepts}
              selectedPayrollConceptId={basicContext.payrollConceptId}
            />
          )
        },
      }),
      perWorkedDayAmountPerDay: f0FormField(z.number().nullable().optional(), {
        label: i18n.t(
          'contract_versions.additional_compensation.create_additional_compensation_wizard.definition.per_worked_day_amount.label'
        ),
        fieldType: 'custom',
        renderIf: () => basicContext.amountStrategyType === PER_WORKED_DAY_STRATEGY,
        render: ({
          value,
          onChange,
          error,
        }: {
          value: unknown
          onChange: (val: unknown) => void
          error?: string
        }) => {
          const numericValue = isNumber(value) ? value : undefined

          return (
            <PerWorkedDayAmountInputWrapper
              value={numericValue}
              onChange={(numberValue) => onChange(numberValue)}
              onClear={() => onChange(null)}
              error={error}
              payrollConcepts={payrollConcepts}
              selectedPayrollConceptId={basicContext.payrollConceptId}
              required
            />
          )
        },
      }),
      perWorkedDayDaysCountHeader: f0FormField(z.string().optional(), {
        label: '',
        fieldType: 'custom',
        renderIf: () => basicContext.amountStrategyType === PER_WORKED_DAY_STRATEGY,
        render: () => <PerWorkedDayDaysCountHeader />,
      }),
      perWorkedDayDaysCountedFrom: f0FormField(z.enum(PER_WORKED_DAY_DAYS_COUNTED_FROM_VALUES), {
        label: i18n.t(
          'contract_versions.additional_compensation.create_additional_compensation_wizard.definition.per_worked_day.days_counted_from.label'
        ),
        fieldType: 'select',
        options: daysCountedFromOptions,
        disabled: true,
        renderIf: () => basicContext.amountStrategyType === PER_WORKED_DAY_STRATEGY,
      }),
      perWorkedDayMinimumHoursPerDay: f0FormField(z.number().nullable().optional(), {
        label: i18n.t(
          'contract_versions.additional_compensation.create_additional_compensation_wizard.definition.per_worked_day.minimum_hours_per_day.label'
        ),
        fieldType: 'duration',
        units: ['hours', 'minutes'],
        size: 'md',
        fields: {
          hours: DURATION_FIELDS.hours,
          minutes: DURATION_FIELDS.minutes,
        },
        renderIf: () => basicContext.amountStrategyType === PER_WORKED_DAY_STRATEGY,
      }),
      perWorkedDayWorkLocations: f0FormField(z.array(z.string()).optional(), {
        label: i18n.t(
          'contract_versions.additional_compensation.create_additional_compensation_wizard.definition.per_worked_day.work_locations.label'
        ),
        fieldType: 'select',
        multiple: true,
        options: workLocationOptions,
        helpText: i18n.t(
          'contract_versions.additional_compensation.create_additional_compensation_wizard.definition.per_worked_day.work_locations.help_text'
        ),
        renderIf: () => basicContext.amountStrategyType === PER_WORKED_DAY_STRATEGY,
      }),
      perWorkedDayIncludedAbsenceLeaveTypeIds: f0FormField(z.array(z.string()).optional(), {
        label: i18n.t(
          'contract_versions.additional_compensation.create_additional_compensation_wizard.definition.per_worked_day.include_absences.label'
        ),
        placeholder: i18n.t(
          'contract_versions.additional_compensation.create_additional_compensation_wizard.definition.per_worked_day.include_absences.placeholder'
        ),
        fieldType: 'select',
        multiple: true,
        options: leaveTypeOptions.map((leaveType) => ({
          value: leaveType.id,
          label: leaveType.label,
        })),
        renderIf: () => basicContext.amountStrategyType === PER_WORKED_DAY_STRATEGY,
      }),
      perWorkedDayEligibleDays: f0FormField(z.array(z.string()).optional(), {
        label: i18n.t(
          'contract_versions.additional_compensation.create_additional_compensation_wizard.definition.per_worked_day.eligible_days.label'
        ),
        placeholder: i18n.t(
          'contract_versions.additional_compensation.create_additional_compensation_wizard.definition.per_worked_day.eligible_days.placeholder'
        ),
        fieldType: 'select',
        multiple: true,
        options: eligibleDayOptions,
        renderIf: () => basicContext.amountStrategyType === PER_WORKED_DAY_STRATEGY,
      }),
    })
    .superRefine((data, context) => {
      const isFixedStrategy = basicContext.amountStrategyType === FIXED_AMOUNT_STRATEGY
      const isPerWorkedDayStrategy = basicContext.amountStrategyType === PER_WORKED_DAY_STRATEGY
      const fixedAmount = normalizeDefinitionNumber(data.fixedAmount)

      if (isPerWorkedDayStrategy) {
        const amountPerDay = normalizeDefinitionNumber(data.perWorkedDayAmountPerDay)

        if (!amountPerDay || amountPerDay <= 0) {
          context.addIssue({
            code: z.ZodIssueCode.custom,
            message: i18n.t('common.required'),
            path: ['perWorkedDayAmountPerDay'],
          })
        }

        return
      }

      if (!isFixedStrategy) return

      if (!fixedAmount || fixedAmount <= 0) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          message: i18n.t('common.required'),
          path: ['fixedAmount'],
        })
      }
    })
}

export { buildDefinitionSchema }
export type { LeaveTypeOption }
